import { WorkOrder } from './work-order.model';
import { FilterOption } from './filter-option';
import { UserDto } from '@app/generated/model';

const open: FilterOption<WorkOrder> = {
  id: 'filter-open',
  label: 'workOrderList.filters.open',
  filter: (workOrder: WorkOrder) => !workOrder.closed,
};

const unreleased: FilterOption<WorkOrder> = {
  id: 'filter-unreleased',
  label: 'workOrderList.filters.unreleased',
  filter: (workOrder: WorkOrder) =>
    !workOrder.closed &&
    workOrder.status === 'UNRELEASED' &&
    workOrder.division === 'SLS' &&
    workOrder.progress !== 100,
};

const released: FilterOption<WorkOrder> = {
  id: 'filter-released',
  label: 'workOrderList.filters.released',
  filter: (workOrder: WorkOrder) =>
    !workOrder.closed && workOrder.status === 'RELEASED' && workOrder.division === 'SLS' && workOrder.progress !== 100,
};

const releasedStatus: FilterOption<WorkOrder> = {
  id: 'filter-released-status',
  label: 'workOrderList.filters.released',
  filter: (workOrder: WorkOrder) =>
    !workOrder.closed && workOrder.isReleased && workOrder.division !== 'SLS' && workOrder.progress !== 100,
};
const unReleasedStatus: FilterOption<WorkOrder> = {
  id: 'filter-released-status',
  label: 'workOrderList.filters.unreleased',
  filter: (workOrder: WorkOrder) =>
    !workOrder.closed && !workOrder.isReleased && workOrder.division !== 'SLS' && workOrder.progress !== 100,
};

const closed: FilterOption<WorkOrder> = {
  id: 'filter-closed',
  label: 'workOrderList.filters.closed',
  filter: (workOrder: WorkOrder) => workOrder.closed || workOrder.progress === 100,
};

const yours: FilterOption<WorkOrder> = {
  id: 'filter-assigned',
  label: 'workOrderList.filters.assigned',
  filter: (workOrder: WorkOrder) => !workOrder.closed && workOrder.isAssigned && workOrder.progress !== 100,
};

const downloaded: FilterOption<WorkOrder> = {
  id: 'filter-downloaded',
  label: 'workOrderList.filters.downloaded',
  filter: (workOrder: WorkOrder) => workOrder.downloaded,
};

const dueThisWeek: FilterOption<WorkOrder> = {
  id: 'filter-due-this-week',
  label: 'workOrderList.filters.dueThisWeek',
  filter: (workOrder: WorkOrder) => {
    const now = new Date();
    const endOfWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 6 - now.getDay());
    const startOfWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6 + now.getDay());
    const woDate = new Date(workOrder.estimatedFinishDate);
    return woDate < endOfWeek && woDate > startOfWeek && !workOrder.closed && workOrder.progress !== 100;
  },
};

const overDue: FilterOption<WorkOrder> = {
  id: 'filter-over-due',
  label: 'workOrderList.filters.overDue',
  filter: (workOrder: WorkOrder) => {
    const now = new Date();
    const woDate = new Date(workOrder.estimatedFinishDate);
    return now > woDate && !workOrder.closed && workOrder.progress !== 100;
  },
};

const addFilter = (filters: FilterOption<WorkOrder>[], filter: FilterOption<WorkOrder>, workOrders: WorkOrder[]) => {
  if (workOrders.some(filter.filter)) {
    filters.push(filter);
  }
};

export const createFilters = (workOrders: WorkOrder[], user: UserDto): FilterOption<WorkOrder>[] => {
  const filters = [];

  // Guard against null/undefined workOrders
  if (!Array.isArray(workOrders)) {
    return filters;
  }

  // Guard against null/undefined user or authorizations
  if (!user || !Array.isArray(user.authorizations)) {
    return filters;
  }

  // Infer role from authorizations - assume the first authorization's role if multiple
  const userRole = user.authorizations?.[0]?.role;

  if (userRole === 'FOREMAN') {
    addFilter(filters, unreleased, workOrders);
    addFilter(filters, released, workOrders);
  }

  if (userRole === 'OPERATOR') {
    addFilter(filters, released, workOrders);
    addFilter(filters, yours, workOrders);
  }
  addFilter(filters, releasedStatus, workOrders);
  addFilter(filters, dueThisWeek, workOrders);
  addFilter(filters, overDue, workOrders);
  addFilter(filters, downloaded, workOrders);
  addFilter(filters, unReleasedStatus, workOrders);
  addFilter(filters, closed, workOrders);

  return filters;
};
